import { useState, useRef } from 'react';
import { Check, Loader2, Upload, X } from 'lucide-react';
import { useAuditStore } from '../../stores/useAuditStore';
import { uploadFile } from '../../api/client';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const ACCEPTED_TYPES = ['.csv', '.xlsx', '.pdf', '.pptx', '.docx', '.md', '.png', '.jpg', '.jpeg'];

/** Brands whose exports we've seen most often — a hint at what to drop here,
 *  not a limit. Anything readable works, hence the trailing note. */
const BRAND_HINTS = [
  { src: '/logos/meta_ads_logo.svg', label: 'Meta Ads' },
  { src: '/logos/tiktok_logo.svg', label: 'TikTok Ads', dark: true },
  { src: '/logos/google_ads_logo.svg', label: 'Google Ads' },
  { src: '/logos/google_analytics_logo.svg', label: 'Google Analytics 4' },
  { src: '/logos/appsflyer_logo.svg', label: 'AppsFlyer' },
  { src: '/logos/linkedIn_logo.svg', label: 'LinkedIn Ads' },
  { src: '/logos/snapchat_logo.svg', label: 'Snapchat Ads' },
  { src: '/logos/x_logo.svg', label: 'X (Twitter) Ads', dark: true },
  { src: '/logos/google-play-console-logo.svg', label: 'Google Play' },
  { src: '/logos/app_store_connect_logo.svg', label: 'App Store Connect', dark: true },
];

type UploadState = 'uploading' | 'done' | 'error';

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

/** Files are identified by name+size: the store holds no id, and index-based
 *  keys break the moment the user removes one mid-upload. */
const fileKey = (name: string, size: number) => `${name}:${size}`;

export default function FileUploadZone() {
  const { uploadedFiles, addFile, removeFile, chatId } = useAuditStore();
  const [dragover, setDragover] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [states, setStates] = useState<Record<string, UploadState>>({});
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = (files: FileList | null) => {
    if (!files) return;
    setUploadError(null);
    Array.from(files).forEach(async (f) => {
      const key = fileKey(f.name, f.size);
      addFile({ name: f.name, size: f.size, type: f.type });
      setStates((prev) => ({ ...prev, [key]: 'uploading' }));
      try {
        // Pass current chatId so the upload is linked to this conversation
        // and gets cascade-deleted when the chat is removed.
        await uploadFile(f, chatId ?? undefined);
        setStates((prev) => ({ ...prev, [key]: 'done' }));
      } catch (err) {
        setStates((prev) => ({ ...prev, [key]: 'error' }));
        setUploadError(err instanceof Error ? err.message : `Failed to upload ${f.name}`);
      }
    });
  };

  return (
    <div
      className={cn(
        'rounded-xl border border-dashed p-4 transition-colors sm:p-[18px]',
        dragover ? 'border-secondary bg-secondary/5' : 'bg-card',
      )}
      onDragOver={(e) => { e.preventDefault(); setDragover(true); }}
      onDragLeave={() => setDragover(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragover(false);
        handleFiles(e.dataTransfer.files);
      }}
    >
      <div className="flex items-start gap-3.5">
        <span className="mt-0.5 flex size-[34px] shrink-0 items-center justify-center rounded-[9px] border bg-muted text-muted-foreground">
          <Upload className="size-4" />
        </span>

        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-bold text-foreground">
            Upload any files for evaluation
          </div>
          <p className="mt-0.5 text-pretty text-[11.5px] leading-snug text-muted-foreground">
            Platform exports, spreadsheets, decks, invoices, screenshots — anything that helps us
            understand the business. CSV, XLSX, PDF, DOCX, PNG, JPG up to 50 MB each.
          </p>

          <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
            <span className="mr-0.5 text-[11px] font-semibold text-muted-foreground">Exports from</span>
            {BRAND_HINTS.map((brand) => (
              <span
                key={brand.label}
                title={brand.label}
                className="flex size-5 shrink-0 items-center justify-center overflow-hidden rounded-[5px] border bg-card"
              >
                <img
                  src={brand.src}
                  alt={brand.label}
                  className={cn('size-3.5 object-contain', brand.dark && 'dark:invert')}
                />
              </span>
            ))}
            <span className="ml-0.5 text-[11px] font-semibold text-muted-foreground">
              and any other platform
            </span>
          </div>
        </div>

        <Button
          size="sm"
          className="mt-0.5 h-auto shrink-0 cursor-pointer rounded-[7px] px-3 py-1 text-[11.5px] font-semibold"
          onClick={() => inputRef.current?.click()}
        >
          Choose files
        </Button>
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPTED_TYPES.join(',')}
        className="hidden"
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = '';
        }}
      />

      {uploadedFiles.length > 0 && (
        <div className="mt-3 flex flex-col gap-1.5">
          {uploadedFiles.map((file, i) => {
            const state = states[fileKey(file.name, file.size)] ?? 'done';
            return (
              <div
                key={`${file.name}-${i}`}
                className="flex items-center gap-2.5 rounded-[7px] border bg-card py-1.5 pl-3 pr-2 text-[11.5px] font-semibold"
              >
                {state === 'uploading' ? (
                  <Loader2 className="size-3.5 shrink-0 animate-spin text-muted-foreground" />
                ) : state === 'error' ? (
                  <span className="flex size-[15px] shrink-0 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                    <X className="size-2.5" strokeWidth={3} />
                  </span>
                ) : (
                  <span className="flex size-[15px] shrink-0 items-center justify-center rounded-full bg-success/10 text-success">
                    <Check className="size-2.5" strokeWidth={3} />
                  </span>
                )}
                <span className={cn('min-w-0 flex-1 truncate', state !== 'done' && 'text-muted-foreground')}>
                  {file.name}
                </span>
                <em className="shrink-0 not-italic font-medium text-muted-foreground">
                  {state === 'uploading' ? 'Uploading…' : formatSize(file.size)}
                </em>
                <button
                  aria-label={`Remove ${file.name}`}
                  className="flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-[5px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  onClick={() => removeFile(i)}
                >
                  <X className="size-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {uploadError && <p className="mt-2 text-xs text-destructive">{uploadError}</p>}
    </div>
  );
}
